// src/components/Auth/AuthShell.tsx
import { useState, useEffect, useRef } from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  Box,
  Chip,
  Menu,
  MenuItem,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Alert,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Home, Settings, ArrowDropDown } from '@mui/icons-material';
import { useModalOpenRegistration } from '../../contexts/ModalOpenContext';
import { getPrimaryRole, hasRole, updateProfile, changePassword } from '../../services/authApi';
import { getRoleDisplayName } from '../../utils/roleDisplayNames';
import { getSharePointBackgroundImageBlob } from '../../services/api';

// Logo path
const fraportLogo = '/Fraport_Logo_USA_60%25_RGB_StoneSansII.png';

// Fraport brand blue color (matching logo)
const FRAPORT_BLUE = '#003366'; // Dark navy blue typical of Fraport branding
// Accent for FRAmework wordmark (subtle gold/amber for contrast on navy)
const FRAMEWORK_ACCENT = '#E8B923';

function clearBodySharePointBackground() {
  const s = document.body.style;
  s.backgroundImage = '';
  s.backgroundSize = '';
  s.backgroundPosition = '';
  s.backgroundRepeat = '';
  s.backgroundAttachment = '';
}

export function AuthShell({ children }: { children: React.ReactNode }) {
  const { user, logout, loading, refreshUser } = useAuth();
  const navigate = useNavigate();
  const backgroundBlobUrlRef = useRef<string | null>(null);
  const [adminMenuAnchor, setAdminMenuAnchor] = useState<null | HTMLElement>(null);
  const [userMenuAnchor, setUserMenuAnchor] = useState<null | HTMLElement>(null);
  const [editNameOpen, setEditNameOpen] = useState(false);
  const [editNameValue, setEditNameValue] = useState('');
  const [editNameSaving, setEditNameSaving] = useState(false);
  const [editNameError, setEditNameError] = useState<string | null>(null);
  const [changePasswordOpen, setChangePasswordOpen] = useState(false);
  useModalOpenRegistration(editNameOpen || changePasswordOpen);
  const [changePasswordValues, setChangePasswordValues] = useState({ currentPassword: '', newPassword: '', confirmPassword: '' });
  const [changePasswordSaving, setChangePasswordSaving] = useState(false);
  const [changePasswordError, setChangePasswordError] = useState<string | null>(null);

  const handleLogout = async () => {
    setUserMenuAnchor(null);
    await logout();
  };

  const handleUserMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setUserMenuAnchor(event.currentTarget);
  };

  const handleUserMenuClose = () => {
    setUserMenuAnchor(null);
  };

  const handleEditNameClick = () => {
    setEditNameValue(user?.displayName ?? user?.email ?? '');
    setEditNameError(null);
    setEditNameOpen(true);
    handleUserMenuClose();
  };

  const handleEditNameSave = async () => {
    if (!user) return;
    setEditNameSaving(true);
    setEditNameError(null);
    try {
      await updateProfile({ displayName: editNameValue.trim() });
      await refreshUser();
      setEditNameOpen(false);
    } catch (err: unknown) {
      const e = err as { message?: string };
      setEditNameError(e?.message ?? 'Failed to update name');
    } finally {
      setEditNameSaving(false);
    }
  };

  const handleChangePasswordClick = () => {
    setChangePasswordValues({ currentPassword: '', newPassword: '', confirmPassword: '' });
    setChangePasswordError(null);
    setChangePasswordOpen(true);
    handleUserMenuClose();
  };

  const handleChangePasswordSubmit = async () => {
    const { currentPassword, newPassword, confirmPassword } = changePasswordValues;
    if (!currentPassword || !newPassword) {
      setChangePasswordError('Current password and new password are required.');
      return;
    }
    if (newPassword !== confirmPassword) {
      setChangePasswordError('New password and confirmation do not match.');
      return;
    }
    setChangePasswordSaving(true);
    setChangePasswordError(null);
    try {
      await changePassword({ currentPassword, newPassword });
      setChangePasswordOpen(false);
      sessionStorage.setItem('passwordUpdated', 'true');
      await logout();
    } catch (err: unknown) {
      const e = err as { message?: string };
      setChangePasswordError(e?.message ?? 'Failed to change password');
    } finally {
      setChangePasswordSaving(false);
    }
  };

  const getRoleColor = (role: string) => {
    switch (role) {
      case 'System Admin':
        return 'error';
      case 'Airport Manager':
        return 'warning';
      default:
        return 'default';
    }
  }
  
  // Only System Admin can access admin menu (not Exec Admin or Airport Manager)
  const hasAdminAccess = user && hasRole(user, 'System Admin');
  const primaryRole = getPrimaryRole(user);

  useEffect(() => {
    let cancelled = false;

    if (backgroundBlobUrlRef.current) {
      URL.revokeObjectURL(backgroundBlobUrlRef.current);
      backgroundBlobUrlRef.current = null;
    }

    if (loading || !user) {
      clearBodySharePointBackground();
      return;
    }

    (async () => {
      const blob = await getSharePointBackgroundImageBlob();
      if (cancelled) {
        return;
      }
      if (!blob) {
        clearBodySharePointBackground();
        return;
      }
      const objectUrl = URL.createObjectURL(blob);
      backgroundBlobUrlRef.current = objectUrl;
      const s = document.body.style;
      s.backgroundImage = `url("${objectUrl}")`;
      s.backgroundSize = 'cover';
      s.backgroundPosition = 'center';
      s.backgroundRepeat = 'no-repeat';
      s.backgroundAttachment = 'fixed';
    })();

    return () => {
      cancelled = true;
      if (backgroundBlobUrlRef.current) {
        URL.revokeObjectURL(backgroundBlobUrlRef.current);
        backgroundBlobUrlRef.current = null;
      }
      clearBodySharePointBackground();
    };
  }, [user, loading]);

  const handleAdminMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAdminMenuAnchor(event.currentTarget);
  };

  const handleAdminMenuClose = () => {
    setAdminMenuAnchor(null);
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      <AppBar 
        position="static" 
        elevation={2}
        sx={{ 
          backgroundColor: FRAPORT_BLUE,
        }}
      >
        <Toolbar sx={{ justifyContent: 'space-between', px: { xs: 1, sm: 2 } }}>
          {/* Left side: Logo and FRAmework */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1, sm: 2 } }}>
            <Box
              component="img"
              src={fraportLogo}
              alt="Fraport Logo"
              sx={{ height: { xs: 32, sm: 40 }, mr: { xs: 1, sm: 2 }, cursor: 'pointer' }}
              onClick={() => navigate('/main')}
            />
            <Typography
              variant="h6"
              component="div"
              sx={{
                display: 'flex',
                alignItems: 'baseline',
                letterSpacing: '0.04em',
                fontSize: { xs: '1.15rem', sm: '1.5rem' },
                fontWeight: 700,
                color: 'white',
                textShadow: '0 1px 2px rgba(0,0,0,0.2)',
                borderBottom: '2px solid',
                borderColor: FRAMEWORK_ACCENT,
                pb: 0.25,
                cursor: 'pointer',
              }}
              onClick={() => navigate('/main')}
            >
              <Box
                component="span"
                sx={{
                  color: FRAMEWORK_ACCENT,
                  fontWeight: 800,
                  letterSpacing: '0.12em',
                  mr: 0.25,
                }}
              >
                FRA
              </Box>
              <Box component="span" sx={{ fontWeight: 600, letterSpacing: '0.02em' }}>
                mework
              </Box>
            </Typography>
          </Box>

          {/* Right side: Icons and User Profile */}
          {!loading && user && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {/* Home Icon */}
              <IconButton
                color="inherit"
                onClick={() => navigate('/main')}
                sx={{ 
                  color: 'white',
                  '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.1)' }
                }}
                aria-label="Dashboard"
              >
                <Home />
              </IconButton>

              {/* System Admin Icon with Dropdown */}
              {hasAdminAccess && (
                <>
                  <IconButton
                    color="inherit"
                    onClick={handleAdminMenuOpen}
                    sx={{ 
                      color: 'white',
                      '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.1)' }
                    }}
                    aria-label="System Admin"
                  >
                    <Settings />
                    <ArrowDropDown sx={{ ml: -0.5, fontSize: '1rem' }} />
                  </IconButton>
                  <Menu
                    anchorEl={adminMenuAnchor}
                    open={Boolean(adminMenuAnchor)}
                    onClose={handleAdminMenuClose}
                    anchorOrigin={{
                      vertical: 'bottom',
                      horizontal: 'right',
                    }}
                    transformOrigin={{
                      vertical: 'top',
                      horizontal: 'right',
                    }}
                  >
                    <MenuItem onClick={() => { handleAdminMenuClose(); navigate('/admin/users'); }}>
                      User Management
                    </MenuItem>
                    <MenuItem onClick={() => { handleAdminMenuClose(); navigate('/admin/airports'); }}>
                      Airport Management
                    </MenuItem>
                    <MenuItem onClick={() => { handleAdminMenuClose(); navigate('/admin/system-stats'); }}>
                      System Stats
                    </MenuItem>
                    <MenuItem onClick={() => { handleAdminMenuClose(); navigate('/admin/system-health'); }}>
                      System Health
                    </MenuItem>
                    <MenuItem onClick={() => { handleAdminMenuClose(); navigate('/admin/sharepoint-libraries'); }}>
                      SharePoint Libraries
                    </MenuItem>
                    <MenuItem onClick={() => { handleAdminMenuClose(); navigate('/admin/holidays'); }}>
                      Holiday Management
                    </MenuItem>
                  </Menu>
                </>
              )}

              {/* User Name - Dropdown menu: Edit name, Change password, Log out */}
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  cursor: 'pointer',
                  px: 1,
                  py: 0.5,
                  borderRadius: 1,
                  '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.1)' }
                }}
                onClick={handleUserMenuOpen}
                aria-controls={userMenuAnchor ? 'user-menu' : undefined}
                aria-haspopup="true"
                aria-expanded={Boolean(userMenuAnchor)}
              >
                <Typography
                  variant="body2"
                  sx={{
                    color: 'white',
                    fontSize: { xs: '0.75rem', sm: '0.875rem' }
                  }}
                >
                  {user.displayName || user.email}
                </Typography>
                <ArrowDropDown sx={{ color: 'white', fontSize: '1.2rem' }} />
                {primaryRole && (
                  <Chip
                    label={getRoleDisplayName(primaryRole)}
                    size="small"
                    color={getRoleColor(primaryRole) as 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning'}
                    sx={{
                      height: 24,
                      fontSize: '0.7rem',
                      color: 'white',
                      '& .MuiChip-label': {
                        color: 'white',
                      },
                    }}
                  />
                )}
              </Box>
              <Menu
                id="user-menu"
                anchorEl={userMenuAnchor}
                open={Boolean(userMenuAnchor)}
                onClose={handleUserMenuClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              >
                <MenuItem onClick={handleEditNameClick}>Edit name</MenuItem>
                <MenuItem onClick={handleChangePasswordClick}>Change password</MenuItem>
                <MenuItem onClick={handleLogout}>Log out</MenuItem>
              </Menu>
            </Box>
          )}
        </Toolbar>
      </AppBar>

      {/* Edit name dialog */}
      <Dialog open={editNameOpen} onClose={() => !editNameSaving && setEditNameOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Edit name</DialogTitle>
        <DialogContent>
          {editNameError && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setEditNameError(null)}>
              {editNameError}
            </Alert>
          )}
          <TextField
            autoFocus
            margin="dense"
            label="Display name"
            fullWidth
            variant="outlined"
            value={editNameValue}
            onChange={(e) => setEditNameValue(e.target.value)}
            disabled={editNameSaving}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditNameOpen(false)} disabled={editNameSaving}>Cancel</Button>
          <Button onClick={handleEditNameSave} variant="contained" disabled={editNameSaving}>
            {editNameSaving ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Change password dialog */}
      <Dialog open={changePasswordOpen} onClose={() => !changePasswordSaving && setChangePasswordOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Change password</DialogTitle>
        <DialogContent>
          {changePasswordError && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setChangePasswordError(null)}>
              {changePasswordError}
            </Alert>
          )}
          <TextField
            autoFocus
            margin="dense"
            label="Current password"
            type="password"
            fullWidth
            variant="outlined"
            value={changePasswordValues.currentPassword}
            onChange={(e) => setChangePasswordValues((prev) => ({ ...prev, currentPassword: e.target.value }))}
            disabled={changePasswordSaving}
          />
          <TextField
            margin="dense"
            label="New password"
            type="password"
            fullWidth
            variant="outlined"
            value={changePasswordValues.newPassword}
            onChange={(e) => setChangePasswordValues((prev) => ({ ...prev, newPassword: e.target.value }))}
            disabled={changePasswordSaving}
          />
          <TextField
            margin="dense"
            label="Confirm new password"
            type="password"
            fullWidth
            variant="outlined"
            value={changePasswordValues.confirmPassword}
            onChange={(e) => setChangePasswordValues((prev) => ({ ...prev, confirmPassword: e.target.value }))}
            disabled={changePasswordSaving}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setChangePasswordOpen(false)} disabled={changePasswordSaving}>Cancel</Button>
          <Button onClick={handleChangePasswordSubmit} variant="contained" disabled={changePasswordSaving}>
            {changePasswordSaving ? 'Updating…' : 'Update password'}
          </Button>
        </DialogActions>
      </Dialog>
      
      <Box component="main" sx={{ flexGrow: 1 }}>
        {children}
      </Box>
    </Box>
  );
}

